import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Link,
  Select,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { InferType } from "yup";
import { omit } from "lodash";

import { handleHookFormHTTPError } from "src/common/form";
import { HTTPError, useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { YupPossiblePhoneNumber } from "src/common/phone-numbers";
import { PhoneNumberInput } from "src/common/PhoneNumbers";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import {
  DEALER_USERS_QUERY_KEY,
  DEALER_USER_QUERY_KEY,
  useDealerUserQuery,
} from "src/routes/DealerOrganization/queries";
import { SomethingWentWrong } from "src/common/ErrorBoundary/SomethingWentWrong";
import { DealerUserRole } from "src/common/types";

const DealerUserValidationSchema = Yup.object().shape({
  firstName: Yup.string().required().label("First Name"),
  lastName: Yup.string().required().label("Last Name"),
  email: Yup.string().required().label("Email"),
  phone: YupPossiblePhoneNumber().required().label("Phone Number"),
  role: Yup.string().required().label("Role"),
});

type DealerUser = InferType<typeof DealerUserValidationSchema>;

export const EditDealerUser = () => {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ky = useKy();

  if (!id || !userId) {
    return (
      <Container maxW="7xl" h="calc(100vh)" centerContent={true}>
        <SomethingWentWrong
          error={new Error("Dealer Organization ID is not provided.")}
        />
      </Container>
    );
  }

  const { control, handleSubmit, formState, reset, setError, getValues } =
    useForm<DealerUser>({
      defaultValues: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
      },
      resolver: yupResolver(DealerUserValidationSchema),
    });

  const getUserQuery = useDealerUserQuery(userId, {
    onSuccess: (data) => {
      if (!formState.isDirty) {
        reset({
          firstName: data.user.firstName || "",
          lastName: data.user.lastName || "",
          email: data.user.email || "",
          phone: data.user.phone || "",
          role: data.role || "",
        });
      }
    },
  });

  const updateUserMutation = useMutation<void, HTTPError, DealerUser>(
    async (values) => {
      await ky.patch(`dealer-users/${userId}`, {
        json: {
          dealerOrganizationId: id,
          ...omit(values, "email"),
        },
      });
    },
    {
      onSuccess: async () => {
        toast({
          description: "User Successfully Updated",
          status: "success",
          duration: 9000,
          isClosable: true,
        });

        await queryClient.invalidateQueries(DEALER_USER_QUERY_KEY);
        await queryClient.invalidateQueries(DEALER_USERS_QUERY_KEY);

        navigate(`/dealers/${id}/users`);
      },
      onError: handleHookFormHTTPError(setError, getValues, toast),
    }
  );

  if (
    updateUserMutation.isLoading ||
    getUserQuery.isLoading ||
    !getUserQuery.isSuccess
  ) {
    return <Loading />;
  }

  if (getUserQuery.isError) {
    return (
      <Container maxW="7xl" h="calc(100vh)" centerContent={true}>
        <SomethingWentWrong
          error={new Error("Unable to load the user details.")}
        />
      </Container>
    );
  }

  return (
    <PageContent>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={`/dealers/${id}/users`}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Edit Dealer User</PageHeaderTitle>
        <PageHeaderSubtitle>
          Enter the new staff member details here
        </PageHeaderSubtitle>
      </PageHeader>
      <form onSubmit={handleSubmit((data) => updateUserMutation.mutate(data))}>
        <Grid gap={4}>
          <GridItem colSpan={1}>
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="firstName">First Name</FormLabel>
                  <Input {...field} id="firstName" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="lastName">Last Name</FormLabel>
                  <Input {...field} id="lastName" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isDisabled
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input {...field} id="email" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="phone">Phone Number</FormLabel>
                  <PhoneNumberInput {...field} control={control} id="phone" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem>
            <Controller
              name="role"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="role">Role</FormLabel>
                  <Select {...field} id="role">
                    <option value={DealerUserRole.admin}>Admin</option>
                    <option value={DealerUserRole.staff}>Staff</option>
                  </Select>
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          {updateUserMutation.isError && updateUserMutation.error && (
            <GridItem colSpan={2}>
              <Alert status="error">
                <AlertIcon />
                There was a problem updating the staff member.
              </Alert>
            </GridItem>
          )}

          <GridItem colSpan={2}>
            <Divider />
          </GridItem>

          <GridItem colSpan={2}>
            <ButtonGroup display="flex" justifyContent="flex-end">
              <Link as={RouterLink} to={`/dealers/${id}/users`}>
                <Button>Cancel</Button>
              </Link>
              <Button
                type="submit"
                colorScheme="brand.blue"
                isLoading={formState.isSubmitting}
              >
                Save
              </Button>
            </ButtonGroup>
          </GridItem>
        </Grid>
      </form>
    </PageContent>
  );
};
